/**
 * Fotos de progreso (Fase 6, 0.14.0; design.md 7.10 y 10.7; Req. 6.17).
 *
 * Aquí viven las decisiones, sin Dexie ni canvas, para poder probarlas:
 * cuánto se reduce cada foto, cómo se nombra dentro del respaldo, qué se
 * verifica al restaurar y cómo se ordena la galería.
 *
 * Reglas que vienen del resto de la app:
 *
 * - **Nada se borra solo.** Una foto que no se puede leer se conserva tal
 *   cual y se dice, como los pesajes dañados (revisión de la 0.4.0).
 * - **El respaldo se verifica.** Cada foto viaja con su SHA-256 y al
 *   restaurar se vuelve a calcular: una foto que no cuadra no entra y se
 *   reporta, en lugar de guardar un archivo corrupto (Req. 6.13, 6.17).
 * - **Siempre hay salida sin fotos.** El respaldo en JSON de siempre sigue
 *   existiendo, porque es el que cabe en el portapapeles y en iCloud sin
 *   pelear con el tamaño.
 */

import type { FechaLocal } from './dates';

export const POSES = ['frente', 'perfil', 'espalda', 'otra'] as const;
export type Pose = (typeof POSES)[number];

export const NOMBRE_POSE: Readonly<Record<Pose, string>> = {
  frente: 'De frente',
  perfil: 'De perfil',
  espalda: 'De espalda',
  otra: 'Otra',
};

/** Lo que se guarda de cada foto; `datos` es el JPEG ya reducido. */
export interface FotoGuardada {
  id: string;
  fecha: FechaLocal;
  momento: string;
  pose: Pose;
  tipo: 'image/jpeg';
  bytes: number;
  sha256: string;
  nota: string | null;
  creadoEn: string;
}

export const NOTA_MAX = 300;

// ---------------------------------------------------------------------------
// Reducir la foto antes de guardarla
// ---------------------------------------------------------------------------

/**
 * Lado mayor al que se reduce. 1,280 px alcanza para ver el cambio en el
 * cuerpo y deja cada foto en unos 200 KB, así que un respaldo con un año de
 * fotos mensuales sigue siendo manejable en el iPhone.
 */
export const LADO_MAXIMO = 1280;
/** Calidad del JPEG: a 0.8 no se ven artefactos y pesa la mitad que a 0.95. */
export const CALIDAD_JPEG = 0.8;

/** Tamaño al que se dibuja una foto, conservando su proporción. */
export function medidaReducida(ancho: number, alto: number, ladoMaximo = LADO_MAXIMO): { ancho: number; alto: number } {
  if (ancho <= 0 || alto <= 0) return { ancho: 0, alto: 0 };
  const mayor = Math.max(ancho, alto);
  if (mayor <= ladoMaximo) return { ancho: Math.round(ancho), alto: Math.round(alto) };
  const factor = ladoMaximo / mayor;
  return { ancho: Math.max(1, Math.round(ancho * factor)), alto: Math.max(1, Math.round(alto * factor)) };
}

export type ArchivoInvalido = 'no_imagen' | 'vacio' | 'enorme';

/** Lo que se acepta al elegir un archivo; por encima de esto ni se intenta leer. */
export const BYTES_MAXIMOS_ORIGEN = 40 * 1024 * 1024;

export const TEXTO_ARCHIVO_INVALIDO: Readonly<Record<ArchivoInvalido, string>> = {
  no_imagen: 'Ese archivo no es una imagen. Elige una foto.',
  vacio: 'Ese archivo está vacío.',
  enorme: 'Esa imagen pesa más de 40 MB. Elige otra o tómala de nuevo.',
};

export function revisarArchivo(archivo: { type: string; size: number }): ArchivoInvalido | null {
  if (archivo.size <= 0) return 'vacio';
  if (!archivo.type.startsWith('image/')) return 'no_imagen';
  if (archivo.size > BYTES_MAXIMOS_ORIGEN) return 'enorme';
  return null;
}

// ---------------------------------------------------------------------------
// La galería
// ---------------------------------------------------------------------------

export interface DiaDeFotos {
  fecha: FechaLocal;
  fotos: FotoGuardada[];
}

/**
 * Las fotos agrupadas por día, el más reciente primero, y dentro de cada día
 * en el orden en que se tomaron.
 */
export function porDia(fotos: readonly FotoGuardada[]): DiaDeFotos[] {
  const dias = new Map<FechaLocal, FotoGuardada[]>();
  for (const f of fotos) {
    const delDia = dias.get(f.fecha);
    if (delDia === undefined) dias.set(f.fecha, [f]);
    else delDia.push(f);
  }
  return [...dias.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([fecha, suyas]) => ({
      fecha,
      fotos: [...suyas].sort((a, b) => (a.momento < b.momento ? -1 : a.momento > b.momento ? 1 : 0)),
    }));
}

/** "3 fotos · 1 de frente, 2 de perfil" */
export function textoDelDia(fotos: readonly FotoGuardada[]): string {
  const cuantas = fotos.length === 1 ? '1 foto' : `${fotos.length} fotos`;
  const cuenta = new Map<Pose, number>();
  for (const f of fotos) cuenta.set(f.pose, (cuenta.get(f.pose) ?? 0) + 1);
  const detalle = POSES.filter((p) => cuenta.has(p))
    .map((p) => `${cuenta.get(p) ?? 0} ${NOMBRE_POSE[p].toLocaleLowerCase('es-MX')}`)
    .join(', ');
  return `${cuantas} · ${detalle}`;
}

/** "1.2 MB en 6 fotos" */
export function textoDelEspacio(fotos: readonly FotoGuardada[]): string {
  const bytes = fotos.reduce((suma, f) => suma + f.bytes, 0);
  const mb = bytes / (1024 * 1024);
  const tamano = mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
  return `${tamano} en ${fotos.length === 1 ? '1 foto' : `${fotos.length} fotos`}`;
}

// ---------------------------------------------------------------------------
// Las fotos dentro del respaldo (design.md 10.7)
// ---------------------------------------------------------------------------

/** La carpeta del ZIP donde van las fotos, y el nombre de cada una. */
export const CARPETA_FOTOS = 'fotos';
export const NOMBRE_DEL_JSON = 'respaldo.json';

/** `fotos/fot_abc123.jpg`; el id manda, así que no hay nombres repetidos. */
export function rutaDeFoto(id: string): string {
  return `${CARPETA_FOTOS}/${id}.jpg`;
}

/** El id a partir de la ruta; null si ese nombre no es de una foto. */
export function idDesdeRuta(ruta: string): string | null {
  const esperado = new RegExp(`^${CARPETA_FOTOS}/([A-Za-z0-9_-]+)\\.jpg$`);
  return esperado.exec(ruta)?.[1] ?? null;
}

export type ProblemaDeFoto =
  /** El JSON la declara pero el ZIP no la trae. */
  | { tipo: 'falta'; id: string }
  /** Está, pero su SHA-256 no es el que dice el JSON. */
  | { tipo: 'no_cuadra'; id: string }
  /** El ZIP la trae y el JSON no la menciona. */
  | { tipo: 'sobra'; id: string };

export interface RevisionDeFotos {
  /** Las que se pueden restaurar: están y su huella cuadra. */
  buenas: string[];
  problemas: ProblemaDeFoto[];
}

/**
 * Compara lo que declara el JSON con lo que trae el ZIP. No decide qué hacer:
 * eso lo hace la restauración, que entra solo las buenas y reporta el resto.
 */
export function revisarFotosDelRespaldo(
  declaradas: readonly Pick<FotoGuardada, 'id' | 'sha256'>[],
  enElArchivo: ReadonlyMap<string, string>,
): RevisionDeFotos {
  const buenas: string[] = [];
  const problemas: ProblemaDeFoto[] = [];
  const vistos = new Set<string>();

  for (const foto of declaradas) {
    vistos.add(foto.id);
    const huella = enElArchivo.get(foto.id);
    if (huella === undefined) problemas.push({ tipo: 'falta', id: foto.id });
    else if (huella !== foto.sha256) problemas.push({ tipo: 'no_cuadra', id: foto.id });
    else buenas.push(foto.id);
  }
  for (const id of enElArchivo.keys()) {
    if (!vistos.has(id)) problemas.push({ tipo: 'sobra', id });
  }
  return { buenas, problemas };
}

/** Lo que se dice de los problemas, sin tecnicismos y sin esconderlos. */
export function textoDeProblemas(problemas: readonly ProblemaDeFoto[]): string | null {
  if (problemas.length === 0) return null;
  const cuantas = (n: number) => (n === 1 ? '1 foto' : `${n} fotos`);
  const faltan = problemas.filter((p) => p.tipo === 'falta').length;
  const noCuadran = problemas.filter((p) => p.tipo === 'no_cuadra').length;
  const sobran = problemas.filter((p) => p.tipo === 'sobra').length;
  return [
    faltan === 0
      ? null
      : `${cuantas(faltan)} que el respaldo menciona no ${faltan === 1 ? 'viene' : 'vienen'} en el archivo`,
    noCuadran === 0
      ? null
      : `${cuantas(noCuadran)} ${noCuadran === 1 ? 'llegó dañada y no se restaura' : 'llegaron dañadas y no se restauran'}`,
    sobran === 0
      ? null
      : `${cuantas(sobran)} ${sobran === 1 ? 'viene' : 'vienen'} en el archivo pero no ${sobran === 1 ? 'está' : 'están'} en la lista`,
  ]
    .filter((x) => x !== null)
    .join('; ');
}
